import type { Arrival } from './types'

/** One rendered board column — a platform/direction group. */
export interface ArrivalGroup {
  /** Uppercased heading, e.g. `Northbound`; empty when TfL omits it. */
  readonly heading: string
  /** e.g. `Platform 9`; empty when the label carries no platform. */
  readonly platform: string
  /** "Towards …" summary from the soonest train that names one. */
  readonly towards: string
  /** Soonest-first, capped at `maxPerGroup`. */
  readonly arrivals: readonly Arrival[]
}

export interface GroupOptions {
  /** Keep only this TfL line id (the page's line); omit for all lines. */
  lineId?: string
  /** Rows per column; the design board shows 4. */
  maxPerGroup?: number
  /** Columns; the design board shows 2. */
  maxGroups?: number
}

/**
 * Groups a station's arrival board into per-platform columns the way
 * the boards render on every platform (web section, upcoming mobile
 * surfaces and widgets): filter to the current line, split by
 * `platformName` ("Northbound - Platform 9" → heading + platform),
 * order columns by their soonest train, cap rows.
 */
export function groupArrivals(
  arrivals: readonly Arrival[],
  options: GroupOptions = {},
): ArrivalGroup[] {
  const maxPerGroup = options.maxPerGroup ?? 4
  const maxGroups = options.maxGroups ?? 2

  const filtered =
    options.lineId === undefined ? arrivals : arrivals.filter((a) => a.lineId === options.lineId)

  const byPlatform = new Map<string, Arrival[]>()
  for (const arrival of filtered) {
    const key = arrival.platformName
    const bucket = byPlatform.get(key)
    if (bucket === undefined) byPlatform.set(key, [arrival])
    else bucket.push(arrival)
  }

  const groups: ArrivalGroup[] = []
  for (const [platformName, bucket] of byPlatform) {
    bucket.sort((a, b) => a.timeToStationSec - b.timeToStationSec)
    const [rawHeading, rawPlatform] = platformName.includes(' - ')
      ? platformName.split(' - ', 2)
      : [platformName, '']
    groups.push({
      heading: (rawHeading ?? '').trim(),
      platform: (rawPlatform ?? '').trim(),
      towards: bucket.find((a) => a.towards !== '')?.towards ?? '',
      arrivals: bucket.slice(0, maxPerGroup),
    })
  }
  groups.sort(
    (a, b) => (a.arrivals[0]?.timeToStationSec ?? 0) - (b.arrivals[0]?.timeToStationSec ?? 0),
  )
  return groups.slice(0, maxGroups)
}
